/**
 * Per-instance reliability state — shared selector module.
 *
 * Source of truth for "what's the latest reporting timestamp per KPI instance,
 * and is that instance currently on-time / due / overdue?"
 *
 * Two surfaces consume this:
 *   1. App.tsx — assembles `expected_instances` for the data-reliability engine
 *      so per-instance Completeness / Timeliness operate on the same data.
 *   2. The Action Center's Data tab inside ContentApprovalsPage
 *      (DEV-681 / DEV-526) — assembles per-project rows for the KPI Data
 *      Entries Due section, filtered to the current user's assignments
 *      and classified by lateness state.
 *
 * Pure functions only — no React, no DOM, no I/O. Same inputs + same as_of_date
 * always produce same outputs. Both call sites pass their existing API stream
 * references in; this module never fetches.
 *
 * Lateness rule mirrors `src/kpi-engine/dataReliability/components/{completeness,timeliness}.ts`.
 * If you change the rule, change it there first; this module reads it.
 *
 * See: DEV-851 (engine v2 per-instance refactor), DEV-681 (Action Center),
 *      DEV-529 (notification trigger uses the same classification).
 */

import type {
  ExpectedInstanceInput,
  ProgressEntryInput,
  Cadence,
} from '../../kpi-engine/dataReliability';
import type { SubKpiInstance } from '../ProjectConfig/projectKpiConfigOverrides';
import { excludeHse } from '../missionSubkpiFilter';

/* ------------------------------------------------------------------------- */
/*  Types                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Latest reporting timestamps for a single (project, instance) pair.
 * Created from the raw API series streams; consumed by the per-project
 * builder below.
 */
export interface LatestPerInstance {
  reporting_date: string;
  created_at: string;
}

/**
 * Map keyed by `${projectGuid}|${instanceId}` (both upper-cased) → latest
 * timestamps. Built once per stream by the index helpers below.
 */
export type LatestIndex = Record<string, LatestPerInstance>;

/**
 * State of a single KPI instance relative to as_of_date. Matches the engine's
 * per-instance classification + the bell's trigger threshold (due + overdue
 * count toward the bell; on-time and N/A do not).
 *
 * See DEV-529 for the bell trigger semantics; see completeness.ts /
 * timeliness.ts for the engine's identical math.
 */
export type InstanceLatenessState = 'on-time' | 'due' | 'overdue' | 'na';

/**
 * Reliability row state shown in the DataReliabilityTab matrix Status column.
 *
 * Combines TWO axes into a single label:
 *   - Recency (was the latest submission within cadence?)
 *   - Coverage (are there missing periods historically?)
 *
 * Previously the matrix collapsed these into a single binary "On time"
 * which was misleading: a row with 8 missing periods could still read
 * "On time" because its latest submission was recent. The taxonomy below
 * keeps each state honest about what it means and what action it implies.
 *
 *   current        — recency OK, no backlog. No action.
 *   backlogged     — recency OK, but missing > 0 periods. Backfill historical gaps.
 *   late           — latest beyond cadence but within grace; missing <= 1.
 *                    Just slipped this period. Enter the missing data.
 *   falling-behind — latest beyond grace OR many missing periods.
 *                    Pattern is degrading. Escalate or reassign.
 *   never-reported — no submissions on record (and expected_periods > 0).
 *   not-yet-due    — cadence hasn't fired yet (expected_periods === 0).
 *   na             — one-time milestone, HSE event-driven, or otherwise
 *                    not on a recurring cadence.
 *
 * Color mapping (Management tab palette):
 *   current        → green
 *   backlogged     → yellow
 *   late           → yellow
 *   falling-behind → red
 *   never-reported → red
 *   not-yet-due    → grey
 *   na             → grey
 */
export type ReliabilityRowState =
  | 'current'
  | 'backlogged'
  | 'late'
  | 'falling-behind'
  | 'never-reported'
  | 'not-yet-due'
  | 'na';

export interface ClassifyInstanceArgs {
  instance: ExpectedInstanceInput;
  /** ISO YYYY-MM-DD — typically today, or a debug override. */
  asOfDate: string;
  /** Grace falls back to this when the instance has no per-instance grace_days. */
  defaultGraceDays: number;
}

/**
 * Bundle of per-stream API data that App.tsx and the Action Center Data
 * tab both have access to. The selectors take this directly so callers
 * don't have to reshape data first.
 */
export interface PerInstanceStateInputs {
  /** apiScheduleData — flat list of schedule series points (seed shape). */
  scheduleRows: any[];
  /** apiFinanceData — flat list of budget/finance series points (seed shape). */
  financeRows: any[];
  /** missionRawPoints — flat list of mission metric points from useApiMissionData. */
  missionPoints: any[];
}

/* ------------------------------------------------------------------------- */
/*  Cadence helper                                                           */
/* ------------------------------------------------------------------------- */

/** Map cadence string → expected days between consecutive reports. */
export function cadenceToDays(cadence: string): number {
  switch (cadence) {
    case 'daily':     return 1;
    case 'weekly':    return 7;
    case 'quarterly': return 91;
    case 'monthly':
    default:          return 30;
  }
}

/* ------------------------------------------------------------------------- */
/*  Date helpers                                                             */
/* ------------------------------------------------------------------------- */

/** Days between two ISO strings (positive when b is after a). */
function daysBetween(a: string, b: string): number {
  const ma = Date.parse(a);
  const mb = Date.parse(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

/* ------------------------------------------------------------------------- */
/*  Latest-per-instance indexers (one pass per stream)                       */
/* ------------------------------------------------------------------------- */

/**
 * Build the latest-per-instance index for Schedule.
 * Filters rows where `actual_complete` is null (baseline-only rows aren't
 * a real submission). Keys by `${projectGuid}|${instanceGuid}` upper-cased.
 */
export function indexLatestSchedule(rows: any[] | undefined | null): LatestIndex {
  const idx: LatestIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_complete == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    const createdAt = String((row as any)?.created_at || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    const existing = idx[key];
    if (!existing || reportingDate > existing.reporting_date) {
      idx[key] = { reporting_date: reportingDate, created_at: createdAt };
    }
  }
  return idx;
}

/**
 * Build the latest-per-instance index for Finance/Budget.
 * Filters rows where `actual_spend` is null. Same keying as Schedule.
 */
export function indexLatestFinance(rows: any[] | undefined | null): LatestIndex {
  const idx: LatestIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_spend == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    const createdAt = String((row as any)?.created_at || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    const existing = idx[key];
    if (!existing || reportingDate > existing.reporting_date) {
      idx[key] = { reporting_date: reportingDate, created_at: createdAt };
    }
  }
  return idx;
}

/**
 * Build the latest-per-instance index for Mission.
 * Mission keys on `subKpiKey` (upper-cased) instead of an instance GUID.
 * Prefers `enteredAt` (user-facing wall-clock) over `createdAt` (DB insert).
 */
export function indexLatestMission(points: any[] | undefined | null): LatestIndex {
  const idx: LatestIndex = {};
  for (const point of (points || []) as any[]) {
    const guid = String((point as any)?.projectId || '').trim().toUpperCase();
    const skKey = String((point as any)?.subKpiKey || '').trim().toUpperCase();
    if (!guid || !skKey) continue;
    const reportingDate = String((point as any)?.reportingDate || '');
    const createdAt = String((point as any)?.enteredAt || (point as any)?.createdAt || '');
    if (!reportingDate) continue;
    const key = `${guid}|${skKey}`;
    const existing = idx[key];
    if (!existing || reportingDate > existing.reporting_date) {
      idx[key] = { reporting_date: reportingDate, created_at: createdAt };
    }
  }
  return idx;
}

/**
 * Convenience: build all three indices from one inputs bundle.
 */
export function buildLatestIndices(
  inputs: PerInstanceStateInputs,
): { schedule: LatestIndex; finance: LatestIndex; mission: LatestIndex } {
  return {
    schedule: indexLatestSchedule(inputs.scheduleRows),
    finance:  indexLatestFinance(inputs.financeRows),
    mission:  indexLatestMission(inputs.missionPoints),
  };
}

/* ------------------------------------------------------------------------- */
/*  All-reporting-dates-per-instance indexers (for coverage computation)     */
/* ------------------------------------------------------------------------- */

/**
 * Map keyed by `${projectGuid}|${instanceId}` (both upper-cased) → an array
 * of every reporting_date the instance has on record. Used to count
 * `reported_periods` vs `expected_periods` for the matrix's Missing column
 * and the engine's fractional completeness rule.
 *
 * Same keying as LatestIndex; both can be built from the same raw stream
 * in one pass if needed, but they're separate functions here so callers
 * that only need one don't pay for the other.
 */
export type AllDatesIndex = Record<string, string[]>;

export function indexAllDatesSchedule(rows: any[] | undefined | null): AllDatesIndex {
  const idx: AllDatesIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_complete == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push(reportingDate);
  }
  return idx;
}

export function indexAllDatesFinance(rows: any[] | undefined | null): AllDatesIndex {
  const idx: AllDatesIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_spend == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push(reportingDate);
  }
  return idx;
}

export function indexAllDatesMission(points: any[] | undefined | null): AllDatesIndex {
  const idx: AllDatesIndex = {};
  for (const point of (points || []) as any[]) {
    const guid = String((point as any)?.projectId || '').trim().toUpperCase();
    const skKey = String((point as any)?.subKpiKey || '').trim().toUpperCase();
    if (!guid || !skKey) continue;
    const reportingDate = String((point as any)?.reportingDate || '');
    if (!reportingDate) continue;
    const key = `${guid}|${skKey}`;
    (idx[key] ||= []).push(reportingDate);
  }
  return idx;
}

export function buildAllDatesIndices(
  inputs: PerInstanceStateInputs,
): { schedule: AllDatesIndex; finance: AllDatesIndex; mission: AllDatesIndex } {
  return {
    schedule: indexAllDatesSchedule(inputs.scheduleRows),
    finance:  indexAllDatesFinance(inputs.financeRows),
    mission:  indexAllDatesMission(inputs.missionPoints),
  };
}

/* ------------------------------------------------------------------------- */
/*  Baseline-dates-per-instance indexers (for curve-based coverage)           */
/* ------------------------------------------------------------------------- */

/**
 * Map of per-instance baseline reporting dates — same shape as AllDatesIndex
 * but WITHOUT the actual_complete / actual_spend filter. Every baseline row
 * in the series-points table is counted as an expected reporting date,
 * regardless of whether an actual value has been submitted yet.
 *
 * Used by buildExpectedInstancesForProject (when supplied) to compute
 * coverage against the user's actual baseline curve instead of abstract
 * cadence buckets. This makes the matrix agree with the data-entry grid:
 * if the grid has a row for 2026-04-20, that's an expected period; if it
 * doesn't, that date isn't expected.
 *
 * Schedule and Finance have baseline curves and use this. Mission does not
 * — missionRawPoints are reports, not baseline-row pre-allocations — so
 * the mission selector keeps the cadence-bucket fallback in
 * computeReliabilityCoverage().
 */
export function indexBaselineDatesSchedule(rows: any[] | undefined | null): AllDatesIndex {
  const idx: AllDatesIndex = {};
  for (const row of (rows || []) as any[]) {
    // No actual_complete filter — we count baseline rows whether or not
    // they have a submitted value yet. The presence of a row defines an
    // expected reporting period.
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push(reportingDate);
  }
  return idx;
}

export function indexBaselineDatesFinance(rows: any[] | undefined | null): AllDatesIndex {
  const idx: AllDatesIndex = {};
  for (const row of (rows || []) as any[]) {
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    if (!reportingDate) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push(reportingDate);
  }
  return idx;
}

export function buildBaselineDatesIndices(
  inputs: PerInstanceStateInputs,
): { schedule: AllDatesIndex; finance: AllDatesIndex } {
  return {
    schedule: indexBaselineDatesSchedule(inputs.scheduleRows),
    finance:  indexBaselineDatesFinance(inputs.financeRows),
  };
}

/* ------------------------------------------------------------------------- */
/*  Lag-per-instance indexers (for the matrix "Time" column)                  */
/* ------------------------------------------------------------------------- */

/**
 * Per-instance list of (reporting_date, created_at) tuples for submitted
 * actuals. Used to compute average submission lag — days between when a
 * report was DUE (reporting_date) and when it was SUBMITTED (created_at).
 *
 * Filters: only rows with both a reporting_date AND a non-null actual
 * count as "submissions" with measurable lag. Baseline-only rows have no
 * submission timing.
 */
export type LagIndex = Record<string, Array<{ reporting_date: string; created_at: string }>>;

function indexLagSchedule(rows: any[] | undefined | null): LagIndex {
  const idx: LagIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_complete == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    const createdAt = String((row as any)?.created_at || '');
    if (!reportingDate || !createdAt) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push({ reporting_date: reportingDate, created_at: createdAt });
  }
  return idx;
}

function indexLagFinance(rows: any[] | undefined | null): LagIndex {
  const idx: LagIndex = {};
  for (const row of (rows || []) as any[]) {
    if ((row as any)?.actual_spend == null) continue;
    const guid = String((row as any)?.project_GUID || '').trim().toUpperCase();
    const iGuid = String((row as any)?.instance_guid || '').trim().toUpperCase();
    if (!guid || !iGuid) continue;
    const reportingDate = String((row as any)?.reporting_date || '');
    const createdAt = String((row as any)?.created_at || '');
    if (!reportingDate || !createdAt) continue;
    const key = `${guid}|${iGuid}`;
    (idx[key] ||= []).push({ reporting_date: reportingDate, created_at: createdAt });
  }
  return idx;
}

function indexLagMission(points: any[] | undefined | null): LagIndex {
  const idx: LagIndex = {};
  for (const point of (points || []) as any[]) {
    const guid = String((point as any)?.projectId || '').trim().toUpperCase();
    const skKey = String((point as any)?.subKpiKey || '').trim().toUpperCase();
    if (!guid || !skKey) continue;
    const reportingDate = String((point as any)?.reportingDate || '');
    // Mission points carry submission timestamp in enteredAt (preferred)
    // or createdAt (fallback). enteredAt = user-facing wall clock,
    // createdAt = DB insert. Prefer enteredAt for an honest "when did
    // the user actually submit this" measure.
    const createdAt = String((point as any)?.enteredAt || (point as any)?.createdAt || '');
    if (!reportingDate || !createdAt) continue;
    const key = `${guid}|${skKey}`;
    (idx[key] ||= []).push({ reporting_date: reportingDate, created_at: createdAt });
  }
  return idx;
}

export function buildLagIndices(
  inputs: PerInstanceStateInputs,
): { schedule: LagIndex; finance: LagIndex; mission: LagIndex } {
  return {
    schedule: indexLagSchedule(inputs.scheduleRows),
    finance:  indexLagFinance(inputs.financeRows),
    mission:  indexLagMission(inputs.missionPoints),
  };
}

/** Default rolling window for avg-lag computation. Submissions older
 * than this (by created_at) don't contribute to the average — recent
 * habit dominates. 90 days = one quarter, a natural project-review
 * cadence. See Phase B in Task #76 for the manual-reset override. */
export const AVG_LAG_WINDOW_DAYS_DEFAULT = 90;

/**
 * Pure: compute the average submission lag in days across a set of
 * (reporting_date, created_at) tuples.
 *
 * lag_i = max(0, days(created_at - reporting_date))
 *   — negative lags (submitted before due) clamp to 0.
 *   — zero means on-time submission.
 *   — positive means late by that many days.
 *
 * Rolling-window filter: only tuples whose created_at is within
 * `windowDays` of `asOfDate` count. Older submissions age out and
 * stop dragging the average. Pass asOfDate=undefined to disable the
 * window (computes across all tuples — kept for unit-test simplicity).
 *
 * Returns null when no tuples fall within the window (matrix renders "—").
 *
 * Note: this measures HABIT (how late are submissions on average)
 * separate from the per-row Comp. state (which measures completeness +
 * recency). The two answer different questions: "what should I do?"
 * vs "how disciplined is the reporting?"
 */
export function computeAvgLagDays(
  tuples: Array<{ reporting_date: string; created_at: string }> | undefined | null,
  asOfDate?: string,
  windowDays: number = AVG_LAG_WINDOW_DAYS_DEFAULT,
): number | null {
  if (!tuples || tuples.length === 0) return null;
  const msPerDay = 1000 * 60 * 60 * 24;
  // Window cutoff: any submission created BEFORE this timestamp is
  // outside the rolling window and ignored. When asOfDate is missing
  // the window is disabled (cutoff = -Infinity, all tuples count).
  let cutoffMs = -Infinity;
  if (asOfDate) {
    const asOfMs = Date.parse(String(asOfDate).slice(0, 10));
    if (Number.isFinite(asOfMs) && windowDays > 0) {
      cutoffMs = asOfMs - windowDays * msPerDay;
    }
  }
  let sum = 0;
  let count = 0;
  for (const t of tuples) {
    const rd = Date.parse(String(t?.reporting_date ?? ''));
    const ca = Date.parse(String(t?.created_at ?? ''));
    if (!Number.isFinite(rd) || !Number.isFinite(ca)) continue;
    if (ca < cutoffMs) continue; // outside rolling window — ignore
    const lagMs = ca - rd;
    const lagDays = Math.max(0, lagMs / msPerDay);
    sum += lagDays;
    count += 1;
  }
  if (count === 0) return null;
  return sum / count;
}

/**
 * Curve-based coverage — uses the baseline reporting-date list as the
 * source of truth for "expected periods" instead of cadence math.
 *
 * Returns:
 *   expected_periods  = count of baseline dates on or before asOfDate
 *   reported_periods  = count of reported dates on or before asOfDate
 *   missing_periods   = max(0, expected - reported)
 *
 * ISO date strings (YYYY-MM-DD prefix) compare lexicographically, so we
 * slice both inputs to 10 chars before comparing. Time-suffixed values
 * (YYYY-MM-DDTHH:MM:SS) still compare correctly.
 *
 * Used by buildExpectedInstancesForProject for schedule + finance when
 * baselineDatesIndices is provided. For mission and as a general fallback,
 * computeReliabilityCoverage above handles the cadence-bucket path.
 */
export function computeCurveCoverage(args: {
  baselineDates: string[];
  reportedDates: string[];
  asOfDate: string;
  /**
   * permissible_days_late from the instance config — defaults to 0 if
   * not provided. A baseline date is only counted as expected once it's
   * past its grace window: (today - date) > graceDays. A date within
   * grace is still in the user's submission window and shouldn't count
   * against them yet.
   */
  graceDays?: number;
}): ReliabilityCoverage {
  const asOf = String(args.asOfDate ?? '').slice(0, 10);
  if (!asOf) {
    return { expected_periods: 0, reported_periods: 0, missing_periods: 0 };
  }
  const grace = Math.max(0, Number.isFinite(args.graceDays) ? (args.graceDays as number) : 0);

  // Distinct dates only — a duplicate baseline row for the same date
  // doesn't double-count, and two reports on the same date count as one
  // covered period.
  //
  // Grace handling: a baseline date counts as expected ONLY if it's past
  // its grace window — (asOf - date) > graceDays. A date with gap ≤ grace
  // is still within the user's submission window, not yet missing.
  const baselineSet = new Set<string>();
  for (const d of args.baselineDates ?? []) {
    const iso = String(d ?? '').slice(0, 10);
    if (!iso) continue;
    if (daysBetween(iso, asOf) > grace) baselineSet.add(iso);
  }
  const reportedSet = new Set<string>();
  for (const d of args.reportedDates ?? []) {
    const iso = String(d ?? '').slice(0, 10);
    if (iso && baselineSet.has(iso)) reportedSet.add(iso);
  }
  const expected = baselineSet.size;
  const reported = reportedSet.size;
  return {
    expected_periods: expected,
    reported_periods: reported,
    missing_periods: Math.max(0, expected - reported),
  };
}

/* ------------------------------------------------------------------------- */
/*  Coverage + classification — fractional completeness model                */
/* ------------------------------------------------------------------------- */

/**
 * Pure: compute how many cadence periods have elapsed since the instance
 * started reporting and how many were actually reported.
 *
 * Period bucketing: each cadence period is `cadenceToDays(cadence)` days
 * wide. Bucket index of any date = floor((date - cadenceStart) / cadenceDays).
 * A bucket counts as "reported" if at least one submission falls inside it.
 *
 * Monthly and quarterly cadences use 30 / 91 days as approximations — exact
 * calendar-month boundary tracking is unnecessary for a reliability signal
 * (the user cares about "I have a gap", not "which calendar month").
 *
 * `cadenceStart` is the earliest date the instance was expected to start
 * reporting:
 *   - If tracking_start_date is set on the SubKpiInstance, use it.
 *   - Else use the project's start_date.
 *   - Else use the instance's created_at.
 *   - Caller resolves this; we just take an ISO date.
 *
 * Returns { expected_periods: 0, reported_periods: 0, missing_periods: 0 }
 * when no period has elapsed (cadenceStart in the future) — caller should
 * surface this as "Not yet due", not "Missing 0 of 0".
 */
export interface ReliabilityCoverage {
  expected_periods: number;
  reported_periods: number;
  missing_periods: number;
}

export function computeReliabilityCoverage(args: {
  cadence: string;
  cadenceStart: string | null | undefined;
  asOfDate: string;
  reportedDates: string[];
  /**
   * permissible_days_late — a cadence bucket is only counted as expected
   * once it's past its grace window. The "current" bucket (covering the
   * present moment) and any bucket whose end is within graceDays of today
   * are still in the user's submission window, not yet missing.
   */
  graceDays?: number;
}): ReliabilityCoverage {
  const cadenceDays = cadenceToDays(args.cadence);
  if (cadenceDays <= 0) {
    return { expected_periods: 0, reported_periods: 0, missing_periods: 0 };
  }
  const start = args.cadenceStart ? Date.parse(args.cadenceStart) : NaN;
  const asOf = Date.parse(args.asOfDate);
  if (!Number.isFinite(start) || !Number.isFinite(asOf) || start > asOf) {
    return { expected_periods: 0, reported_periods: 0, missing_periods: 0 };
  }
  const grace = Math.max(0, Number.isFinite(args.graceDays) ? (args.graceDays as number) : 0);
  const msPerDay = 1000 * 60 * 60 * 24;
  // Effective "deadline cutoff" — buckets whose period-start is on or
  // before this are past their grace window. Subtract graceDays from
  // asOf to shift the cutoff backward by the grace allowance.
  const effectiveAsOfMs = asOf - grace * msPerDay;
  if (effectiveAsOfMs < start) {
    return { expected_periods: 0, reported_periods: 0, missing_periods: 0 };
  }
  const elapsedDays = Math.floor((effectiveAsOfMs - start) / msPerDay);
  const expected = Math.max(0, Math.floor(elapsedDays / cadenceDays) + 1);
  if (expected === 0) {
    return { expected_periods: 0, reported_periods: 0, missing_periods: 0 };
  }

  const covered = new Set<number>();
  for (const d of args.reportedDates ?? []) {
    const ts = Date.parse(d);
    if (!Number.isFinite(ts) || ts < start) continue;
    const bucket = Math.floor((ts - start) / msPerDay / cadenceDays);
    if (bucket >= 0 && bucket < expected) covered.add(bucket);
  }

  const reported = covered.size;
  const missing = Math.max(0, expected - reported);
  return { expected_periods: expected, reported_periods: reported, missing_periods: missing };
}

/**
 * Pure: combine recency and coverage into the user-facing matrix status.
 *
 * Decision tree (first match wins):
 *   1. cadence === 'once'       → 'na'  (one-time milestone)
 *   2. expected_periods === 0   → 'not-yet-due'
 *   3. latest === null          → 'never-reported'
 *   4. daysSinceLast <= cadence:
 *        missing === 0          → 'current'
 *        missing >  0           → 'backlogged'
 *   5. daysSinceLast <= cadence + grace AND missing <= 1
 *                                → 'late'         (just slipped, recoverable)
 *   6. otherwise                 → 'falling-behind'
 *
 * HSE event-driven rows are classified externally (the matrix renders them
 * with explicit "N/A — event-driven" treatment; they're not run through
 * this classifier).
 */
export function classifyReliabilityRow(args: {
  cadence: string;
  latestReportingDate: string | null;
  asOfDate: string;
  graceDays: number;
  expectedPeriods: number;
  missingPeriods: number;
}): ReliabilityRowState {
  if (String(args.cadence).toLowerCase() === 'once') return 'na';
  if (args.expectedPeriods === 0) return 'not-yet-due';
  if (!args.latestReportingDate) return 'never-reported';

  const cadenceDays = cadenceToDays(args.cadence);
  const grace = Math.max(0, args.graceDays);
  const daysSince = daysBetween(args.latestReportingDate, args.asOfDate);

  if (daysSince <= cadenceDays) {
    return args.missingPeriods === 0 ? 'current' : 'backlogged';
  }
  if (daysSince <= cadenceDays + grace && args.missingPeriods <= 1) {
    return 'late';
  }
  return 'falling-behind';
}

/* ------------------------------------------------------------------------- */
/*  Per-project ExpectedInstanceInput[] builder                              */
/* ------------------------------------------------------------------------- */

/**
 * Permissive shape the kpiConfigs map yields per project. Each `category_instance`
 * is a SubKpiInstance with cadence + permissible_days_late + identification fields.
 */
export interface ProjectKpiConfig {
  schedule?: SubKpiInstance[];
  finance?: SubKpiInstance[];
  mission?: SubKpiInstance[];
}

export interface BuildExpectedInstancesArgs {
  projectGuid: string;
  kpiCfg: ProjectKpiConfig | undefined;
  latestIndices: { schedule: LatestIndex; finance: LatestIndex; mission: LatestIndex };

  // ── Optional fractional-coverage inputs ────────────────────────
  // When provided, buildExpectedInstancesForProject also populates
  // cadence_start / expected_periods / reported_periods / missing_periods
  // on each returned ExpectedInstanceInput. When omitted, those fields
  // stay undefined and the engine + matrix fall back to the legacy
  // recency-only rule.

  /**
   * Per-instance "all reporting dates" indices, built via
   * buildAllDatesIndices(). Same keying as latestIndices.
   */
  allDatesIndices?: {
    schedule: AllDatesIndex;
    finance: AllDatesIndex;
    mission: AllDatesIndex;
  };

  /**
   * Per-instance baseline reporting-date indices for schedule + finance,
   * built via buildBaselineDatesIndices(). When provided, coverage for
   * those two categories is computed against the actual baseline curve
   * (rows in schedule_series_points / budget_series_points) rather than
   * abstract cadence buckets — agrees with the data-entry grid. Mission
   * has no baseline curve concept; it always falls back to the cadence-
   * bucket computation in computeReliabilityCoverage().
   */
  baselineDatesIndices?: {
    schedule: AllDatesIndex;
    finance: AllDatesIndex;
  };

  /**
   * Per-instance submission-lag indices, built via buildLagIndices(). When
   * provided, buildExpectedInstancesForProject populates avg_lag_days on
   * each returned ExpectedInstanceInput.
   */
  lagIndices?: {
    schedule: LagIndex;
    finance: LagIndex;
    mission: LagIndex;
  };

  /** Project's start_date (ISO). Used as default cadence_start fallback. */
  projectStartDate?: string | null;

  /** ISO YYYY-MM-DD — typically today. Used to compute expected_periods. */
  asOfDate?: string;
}

/**
 * Build the engine's `expected_instances` array for a single project.
 *
 * Mirrors the inline assembly that previously lived in App.tsx (DEV-851 PR2).
 * One ExpectedInstanceInput per configured KPI instance, with the latest
 * reporting timestamps populated from the indices when available.
 *
 * Mission instances key on `outcome_id || instance_guid || display_name`
 * (matching App.tsx's existing convention) — same composite identifier the
 * matrix's `missionInstanceLookup` uses.
 *
 * Mission instances with cadence 'once' are EXCLUDED — one-time milestones
 * don't have a recurring lateness window. They appear in the matrix via
 * deadline_date instead.
 */
export function buildExpectedInstancesForProject(
  args: BuildExpectedInstancesArgs,
): ExpectedInstanceInput[] {
  const {
    projectGuid,
    kpiCfg,
    latestIndices,
    allDatesIndices,
    baselineDatesIndices,
    lagIndices,
    projectStartDate,
    asOfDate,
  } = args;
  const out: ExpectedInstanceInput[] = [];
  if (!projectGuid || !kpiCfg) return out;

  // Task #75 — compute avg submission lag per instance. Pull tuples from
  // the appropriate per-category lag bucket and average them, applying
  // the 90-day rolling window so old back-fill noise ages out and recent
  // habit dominates the score. Returns null when no submissions fall
  // within the window; matrix renders "—".
  const resolveAvgLag = (
    category: 'schedule' | 'finance' | 'mission',
    indexKey: string,
  ): number | null => {
    const bucket =
      category === 'schedule' ? lagIndices?.schedule
      : category === 'finance' ? lagIndices?.finance
      : lagIndices?.mission;
    if (!bucket) return null;
    return computeAvgLagDays(bucket[indexKey], asOfDate);
  };

  const normalizeCadence = (cad: string, fallback: 'weekly' | 'monthly'): Cadence => {
    const c = cad.toLowerCase();
    if (c === 'daily' || c === 'weekly' || c === 'monthly' || c === 'quarterly') return c;
    return fallback;
  };

  // Resolve the cadence-clock start for a given SubKpiInstance. Priority:
  //   1. instance.tracking_start_date (explicit per-instance override)
  //   2. project start_date (the project-level fallback)
  //   3. instance.created_at (when the kpi_config row was inserted)
  // Returns null when none can be resolved — caller treats null as
  // "not-yet-due" (expected_periods will compute as 0).
  const resolveCadenceStart = (inst: any): string | null => {
    const tracking = String(inst?.tracking_start_date ?? '').trim();
    if (tracking) return tracking;
    const proj = String(projectStartDate ?? '').trim();
    if (proj) return proj;
    const created = String(inst?.created_at ?? '').trim();
    if (created) return created.slice(0, 10);
    return null;
  };

  // Enrich an ExpectedInstanceInput with fractional coverage fields. Two
  // paths:
  //
  //   CURVE-BASED (preferred for schedule + finance) — when a baseline-
  //   dates index is provided, count baseline rows on or before asOfDate
  //   as expected_periods, and count those rows that have a non-null
  //   actual as reported_periods. This makes the matrix agree with the
  //   data-entry grid: an expected period exists iff the grid has a row
  //   for it. See computeCurveCoverage().
  //
  //   CADENCE-BUCKET (fallback for mission, or when baseline index is
  //   absent) — generates expected periods from cadenceStart + cadence,
  //   then buckets reported dates into those periods. See
  //   computeReliabilityCoverage().
  //
  // No-op if neither asOfDate nor any index is available.
  const enrichCoverage = (
    base: ExpectedInstanceInput,
    inst: any,
    indexKey: string,
    allDatesBucket: AllDatesIndex | undefined,
    baselineDatesBucket: AllDatesIndex | undefined,
  ): ExpectedInstanceInput => {
    if (!asOfDate) return base;
    const cadenceStart = resolveCadenceStart(inst);
    const reportedDates = allDatesBucket?.[indexKey] ?? [];

    // permissible_days_late from the instance. A baseline date within
    // this grace window isn't yet "expected" — the user still has time
    // to submit. Same number drives Status taxonomy timeliness on the
    // matrix; using it here keeps the count consistent with the label.
    const graceDays = Number.isFinite(base.grace_days)
      ? Math.max(0, base.grace_days as number)
      : 0;

    let coverage: ReliabilityCoverage;
    if (baselineDatesBucket && baselineDatesBucket[indexKey]) {
      // Curve-based: schedule + finance, when the project has a baseline
      // curve in series-points.
      coverage = computeCurveCoverage({
        baselineDates: baselineDatesBucket[indexKey],
        reportedDates,
        asOfDate,
        graceDays,
      });
    } else if (allDatesBucket) {
      // Cadence-bucket fallback: mission, or schedule/finance with no
      // baseline yet (e.g., the user hasn't clicked "Generate baseline").
      coverage = computeReliabilityCoverage({
        cadence: base.cadence,
        cadenceStart,
        asOfDate,
        reportedDates,
        graceDays,
      });
    } else {
      return base;
    }

    return {
      ...base,
      cadence_start: cadenceStart,
      expected_periods: coverage.expected_periods,
      reported_periods: coverage.reported_periods,
      missing_periods: coverage.missing_periods,
    };
  };

  for (const inst of (kpiCfg.schedule || [])) {
    const iGuid = String(inst?.instance_guid || '').trim().toUpperCase();
    if (!iGuid) continue;
    const key = `${projectGuid}|${iGuid}`;
    const latest = latestIndices.schedule[key];
    const base: ExpectedInstanceInput = {
      category: 'schedule',
      instance_id: iGuid,
      cadence: normalizeCadence(String(inst?.cadence || 'weekly'), 'weekly'),
      grace_days: Number.isFinite(inst?.permissible_days_late)
        ? (inst.permissible_days_late as number)
        : undefined,
      latest_reporting_date: latest?.reporting_date ?? null,
      latest_created_at: latest?.created_at ?? null,
    };
    out.push({
      ...enrichCoverage(base, inst, key, allDatesIndices?.schedule, baselineDatesIndices?.schedule),
      avg_lag_days: resolveAvgLag('schedule', key),
    });
  }

  for (const inst of (kpiCfg.finance || [])) {
    const iGuid = String(inst?.instance_guid || '').trim().toUpperCase();
    if (!iGuid) continue;
    const key = `${projectGuid}|${iGuid}`;
    const latest = latestIndices.finance[key];
    const base: ExpectedInstanceInput = {
      category: 'finance',
      instance_id: iGuid,
      cadence: normalizeCadence(String(inst?.cadence || 'weekly'), 'weekly'),
      grace_days: Number.isFinite(inst?.permissible_days_late)
        ? (inst.permissible_days_late as number)
        : undefined,
      latest_reporting_date: latest?.reporting_date ?? null,
      latest_created_at: latest?.created_at ?? null,
    };
    out.push({
      ...enrichCoverage(base, inst, key, allDatesIndices?.finance, baselineDatesIndices?.finance),
      avg_lag_days: resolveAvgLag('finance', key),
    });
  }

  /*
    DEV-822: HSE incident-tracker sub-KPIs are stored under the mission
    category for storage convenience, but they're event-driven (you don't
    "report" an incident on a cadence — incidents happen and get logged
    when they happen). Including them in expected_instances would cause:
      1. Data Reliability Completeness/Timeliness to penalize projects
         for not generating regular incidents — wrong semantic.
      2. The Action Center Data tab to surface HSE rows as "due/overdue",
         pressuring users to either fabricate values or feel guilty
         about safe operations.
    `excludeHse()` is the established helper; same filter is applied on
    every other surface that shows "what the project produces."
  */
  // Cast through any[] — SubKpiInstance type has mission_type_key at runtime
  // but the static type doesn't include it (the field is added by the wizard
  // when the user picks a mission_type plug-in). excludeHse needs to see the
  // field to filter; T extends MissionSubkpiLike degrades to MissionSubkpiLike
  // without the cast and we lose the SubKpiInstance fields downstream.
  const missionInstances = excludeHse((kpiCfg.mission || []) as any[]) as SubKpiInstance[];
  for (const inst of missionInstances) {
    const skKey = String(
      (inst as any)?.outcome_id || inst?.instance_guid || inst?.display_name || ''
    ).trim().toUpperCase();
    if (!skKey) continue;
    const cad = String(inst?.cadence || 'monthly').toLowerCase();
    if (cad === 'once') continue; // one-time milestones — no recurring window
    const key = `${projectGuid}|${skKey}`;
    const latest = latestIndices.mission[key];
    const base: ExpectedInstanceInput = {
      category: 'mission',
      instance_id: skKey,
      cadence: normalizeCadence(cad, 'monthly'),
      grace_days: Number.isFinite(inst?.permissible_days_late)
        ? (inst.permissible_days_late as number)
        : undefined,
      latest_reporting_date: latest?.reporting_date ?? null,
      latest_created_at: latest?.created_at ?? null,
    };
    // Mission has no baseline curve concept — pass undefined for the
    // baseline bucket so enrichCoverage falls through to the cadence-
    // bucket path. (The cadence='once' mission types — binary,
    // milestone_set, incident_count — are already filtered out above.)
    out.push({
      ...enrichCoverage(base, inst, key, allDatesIndices?.mission, undefined),
      avg_lag_days: resolveAvgLag('mission', key),
    });
  }

  return out;
}

/* ------------------------------------------------------------------------- */
/*  Lateness classifier                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Classify an ExpectedInstanceInput against today.
 *
 * Mirrors the matrix's gap-based rule (DEV-851 PR2) and the engine's
 * Completeness rule (per-instance, post-refactor):
 *   - on-time:  daysSinceLast <= cadence
 *   - due:      cadence < daysSinceLast <= cadence + grace
 *   - overdue:  daysSinceLast > cadence + grace
 *   - na:       no latest_reporting_date (never reported)
 *
 * The bell trigger and LeftNav badge count instances in {due, overdue}
 * — see DEV-529 for the rolled-up notification semantics.
 */
export function classifyInstanceLateness(
  args: ClassifyInstanceArgs,
): InstanceLatenessState {
  const { instance, asOfDate, defaultGraceDays } = args;
  if (!instance.latest_reporting_date) return 'na';

  const grace = Number.isFinite(instance.grace_days)
    ? Math.max(0, instance.grace_days as number)
    : Math.max(0, defaultGraceDays);
  const cadence = cadenceToDays(instance.cadence);
  const daysSinceLast = daysBetween(instance.latest_reporting_date, asOfDate);

  if (daysSinceLast <= cadence) return 'on-time';
  if (daysSinceLast <= cadence + grace) return 'due';
  return 'overdue';
}

/**
 * Build a lookup of instance_id (upper-cased) → display label + category.
 *
 * Mirrors the keying convention used by `buildExpectedInstancesForProject`
 * so the Action Center Data tab can render human labels for entries in
 * `data_reliability_expected_instances`. Pure — no I/O.
 *
 * Schedule/Finance: keyed on `instance_guid` (upper-cased).
 * Mission: keyed on `outcome_id || instance_guid || display_name`
 *   (upper-cased) — same composite identifier `buildExpectedInstancesForProject`
 *   and the matrix's missionInstanceLookup use.
 */
export interface InstanceLabel {
  label: string;
  category: 'schedule' | 'finance' | 'mission';
}

export function buildInstanceLabelsForProject(
  kpiCfg: ProjectKpiConfig | undefined,
): Record<string, InstanceLabel> {
  const out: Record<string, InstanceLabel> = {};
  if (!kpiCfg) return out;

  for (const inst of (kpiCfg.schedule || [])) {
    const id = String(inst?.instance_guid || '').trim().toUpperCase();
    if (!id) continue;
    out[id] = {
      label: String(inst?.display_name || id),
      category: 'schedule',
    };
  }
  for (const inst of (kpiCfg.finance || [])) {
    const id = String(inst?.instance_guid || '').trim().toUpperCase();
    if (!id) continue;
    out[id] = {
      label: String(inst?.display_name || id),
      category: 'finance',
    };
  }
  // Same HSE exclusion as buildExpectedInstancesForProject — keeps the
  // labels map aligned with what the actionable filter will surface.
  for (const inst of excludeHse((kpiCfg.mission || []) as any[]) as SubKpiInstance[]) {
    const id = String(
      (inst as any)?.outcome_id || inst?.instance_guid || inst?.display_name || ''
    ).trim().toUpperCase();
    if (!id) continue;
    out[id] = {
      label: String(inst?.display_name || id),
      category: 'mission',
    };
  }
  return out;
}

/**
 * Convenience: count actionable (due + overdue) instances per project.
 * Used by the future ActionCenter and DEV-529's count endpoint.
 */
export function countActionableByProject(
  expectedByProject: Record<string, ExpectedInstanceInput[]>,
  asOfDate: string,
  defaultGraceDays: number,
): Record<string, { total: number; due: number; overdue: number }> {
  const out: Record<string, { total: number; due: number; overdue: number }> = {};
  for (const [guid, instances] of Object.entries(expectedByProject)) {
    let due = 0;
    let overdue = 0;
    for (const inst of instances) {
      const state = classifyInstanceLateness({
        instance: inst,
        asOfDate,
        defaultGraceDays,
      });
      if (state === 'due') due += 1;
      else if (state === 'overdue') overdue += 1;
    }
    out[guid] = { total: due + overdue, due, overdue };
  }
  return out;
}

/* ------------------------------------------------------------------------- */
/*  Engine ProgressEntryInput[] builder (Outlook signals)                    */
/* ------------------------------------------------------------------------- */

/**
 * Build the engine's `progress_entries` flat array per project. Used by the
 * Outlook components (entry-latency trend, etc.). Outlook is N/A in v1, but
 * the input is preserved for forward compatibility.
 *
 * This is the OLD entry shape — flat list of every (category, reporting_date,
 * created_at) tuple. The per-instance refactor moved Now's components onto
 * `expected_instances`, but Outlook still iterates entries.
 */
export function buildProgressEntriesByProject(
  inputs: PerInstanceStateInputs,
): Record<string, ProgressEntryInput[]> {
  const out: Record<string, ProgressEntryInput[]> = {};

  const push = (
    guid: string,
    category: ProgressEntryInput['category'],
    reportingDate: string,
    createdAt: string,
  ) => {
    if (!guid || !reportingDate || !createdAt) return;
    (out[guid] ||= []).push({ category, reporting_date: reportingDate, created_at: createdAt });
  };

  for (const row of (inputs.scheduleRows || []) as any[]) {
    if ((row as any)?.actual_complete == null) continue;
    push(
      String((row as any)?.project_GUID || '').trim().toUpperCase(),
      'schedule',
      String((row as any)?.reporting_date || ''),
      String((row as any)?.created_at || ''),
    );
  }
  for (const row of (inputs.financeRows || []) as any[]) {
    if ((row as any)?.actual_spend == null) continue;
    push(
      String((row as any)?.project_GUID || '').trim().toUpperCase(),
      'finance',
      String((row as any)?.reporting_date || ''),
      String((row as any)?.created_at || ''),
    );
  }
  for (const point of (inputs.missionPoints || []) as any[]) {
    push(
      String((point as any)?.projectId || '').trim().toUpperCase(),
      'mission',
      String((point as any)?.reportingDate || ''),
      String((point as any)?.enteredAt || (point as any)?.createdAt || ''),
    );
  }

  return out;
}
